import Axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type CustomParamsSerializer
} from "axios";
import type {
  PureHttpError,
  PureHttpRequestConfig,
  PureHttpResponse,
  RequestMethods
} from "./types.d";
import { stringify } from "qs";
import { formatToken, getToken } from "@/utils/auth";
import { useUserStoreHook } from "@/store/modules/user";
import { ElMessage } from "element-plus";

// 相关配置请参考：www.axios-js.com/zh-cn/docs/#axios-request-config-1
const defaultConfig: AxiosRequestConfig = {
  // 请求超时时间
  timeout: 10000,
  headers: {
    Accept: "application/json, text/plain, */*",
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest"
  },
  // 数组格式参数序列化（https://github.com/axios/axios/issues/5142）
  paramsSerializer: {
    serialize: stringify as unknown as CustomParamsSerializer
  }
};

class PureHttp {
  constructor() {
    this.httpInterceptorsRequest();
    this.httpInterceptorsResponse();
  }

  /** `token`过期后，暂存待执行的请求 */
  private static requests = [];

  /** 防止重复刷新`token` */
  private static isRefreshing = false;

  /** 初始化配置对象 */
  private static initConfig: PureHttpRequestConfig = {};

  /** 保存当前`Axios`实例对象 */
  private static axiosInstance: AxiosInstance = Axios.create(defaultConfig);

  /** 重连原始请求 */
  private static retryOriginalRequest(config: PureHttpRequestConfig) {
    return new Promise(resolve => {
      PureHttp.requests.push((token: string) => {
        config.headers["Authorization"] = formatToken(token);
        resolve(config);
      });
    });
  }

  /** 请求拦截 */
  private httpInterceptorsRequest(): void {
    PureHttp.axiosInstance.interceptors.request.use(
      async (config: PureHttpRequestConfig) => {
        // 1. 优先处理回调
        if (typeof config.beforeRequestCallback === "function") {
          config.beforeRequestCallback(config);
        }

        // --- 修复点 1: 更稳健的白名单匹配 ---
        // 使用 includes 确保即使有 query 参数也能匹配
        const whiteList = ["/refresh_token", "/login"];
        if (config.url && whiteList.some(url => config.url.includes(url))) {
          return config;
        }

        const data = getToken();
        if (!data) return config;

        const now = new Date().getTime();
        // 建议留出 10 秒缓冲，不要卡死到最后一毫秒
        const expired = parseInt(data.expires) - now <= 0;

        if (!expired) {
          config.headers["Authorization"] = formatToken(data.accessToken);
          return config;
        }

        // --- 2. Token 过期处理 ---
        if (!PureHttp.isRefreshing) {
          PureHttp.isRefreshing = true;

          // --- 修复点 2: 显式 return 这个 Promise 链 ---
          return useUserStoreHook()
            .handRefreshToken({ refreshToken: data.refreshToken })
            .then(res => {
              // 这里的 res 需要符合 handRefreshToken resolve 出来的结构
              const accessToken = res.data.accessToken;

              // 依次执行队列
              PureHttp.requests.forEach(cb => cb(accessToken));
              PureHttp.requests = [];

              config.headers["Authorization"] = formatToken(accessToken);
              // --- 修复点 3: 必须返回一个新的请求实例 ---
              return PureHttp.axiosInstance(config);
            })
            .catch(err => {
              console.error("RefreshToken Error:", err);
              PureHttp.requests = [];
              useUserStoreHook().logOut();
              return Promise.reject(err);
            })
            .finally(() => {
              PureHttp.isRefreshing = false;
            });
        } else {
          // 3. 正在刷新中，挂起当前请求
          return new Promise(resolve => {
            PureHttp.requests.push((token: string) => {
              config.headers["Authorization"] = formatToken(token);
              // 注意这里：如果是重新发起请求，应该 resolve(PureHttp.axiosInstance(config))
              resolve(PureHttp.axiosInstance(config));
            });
          });
        }
      },
      error => {
        return Promise.reject(error);
      }
    );
  }

  /** 响应拦截 */
  private httpInterceptorsResponse(): void {
    const instance = PureHttp.axiosInstance;
    instance.interceptors.response.use(
      (response: PureHttpResponse) => {
        const $config = response.config;

        // 1. Destructure the backend response structure
        // Adjust 'code', 'msg', 'data' to match your actual API fields
        const { code, message, msg } = response.data;

        // 2. Global Error Handling Logic
        // If code exists and is NOT 20000, show error and reject promise
        if (code !== undefined && code !== 20000) {
          const errorMessage = message || msg || "Error";

          // Prevent duplicate messages if needed, or just show it
          ElMessage.error(`${errorMessage} (${code})`);

          // Optional: specific handling for auth errors (e.g., 401/403)
          if (code === 401) {
            // Handle logout or redirect logic here
          }

          return Promise.reject(new Error(errorMessage));
        }

        // 优先判断post/get等方法是否传入回调，否则执行初始化设置等回调
        if (typeof $config.beforeResponseCallback === "function") {
          $config.beforeResponseCallback(response);
          return response.data;
        }
        if (PureHttp.initConfig.beforeResponseCallback) {
          PureHttp.initConfig.beforeResponseCallback(response);
          return response.data;
        }
        return response.data;
      },
      (error: PureHttpError) => {
        const $error = error;
        $error.isCancelRequest = Axios.isCancel($error);
        // 所有的响应异常 区分来源为取消请求/非取消请求
        // Handle Network Errors (404, 500, timeout)
        const status = error.response?.status;
        let message = "Network Error";
        if (status === 401 || status === 403) {
          // 401：未授权（Token过期或无效）-> 强制登出
          useUserStoreHook().logOut();
          return Promise.reject($error);
        }
        if (status === 404) message = "404 Not Found";
        if (status === 500) message = "500 Server Error";
        if (error.message.includes("timeout")) message = "Request Timeout";

        if (!$error.isCancelRequest) {
          ElMessage.error(message);
        }

        return Promise.reject($error);
      }
    );
  }

  /** 通用请求工具函数 */
  public request<T>(
    method: RequestMethods,
    url: string,
    param?: AxiosRequestConfig,
    axiosConfig?: PureHttpRequestConfig
  ): Promise<T> {
    const config = {
      method,
      url,
      ...param,
      ...axiosConfig
    } as PureHttpRequestConfig;

    // 单独处理自定义请求/响应回调
    return new Promise((resolve, reject) => {
      PureHttp.axiosInstance
        .request(config)
        .then((response: undefined) => {
          resolve(response);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  /** 单独抽离的`post`工具函数 */
  public post<T, P>(
    url: string,
    params?: AxiosRequestConfig<P>,
    config?: PureHttpRequestConfig
  ): Promise<T> {
    return this.request<T>("post", url, params, config);
  }

  /** 单独抽离的`get`工具函数 */
  public get<T, P>(
    url: string,
    params?: AxiosRequestConfig<P>,
    config?: PureHttpRequestConfig
  ): Promise<T> {
    return this.request<T>("get", url, params, config);
  }
}

export const http = new PureHttp();
